'use client';

import { useEffect } from 'react';

import { useSearchParams } from 'next/navigation';

import { getAffiliateByName } from '@/actions';
import { useCheckoutStore } from '@/stores';
import {
  AFFILIATE_COOKIE_OPTIONS,
  AFFILIATE_STORAGE_KEY,
  type AffiliateStoredData,
  parseAffiliateData,
} from '@/utils/affiliate';
import { getCookie, removeCookie, setCookie } from '@/utils/cookies';

const UTM_STORAGE_KEY = 'ilottoUtmData';
const HOURS_48_MS = 48 * 60 * 60 * 1000;

export function Affiliate() {
  const searchParams = useSearchParams();
  const setAffiliateData = useCheckoutStore((state) => state.setAffiliateData);
  const setUtmData = useCheckoutStore((state) => state.setUtmData);

  useEffect(() => {
    const affiliateSlug = searchParams.get('afiliado');
    const id = searchParams.get('id');
    const utm_source = searchParams.get('utm_source');
    const utm_medium = searchParams.get('utm_medium');
    const utm_campaign = searchParams.get('utm_campaign');
    const utm_term = searchParams.get('utm_term');
    const utm_content = searchParams.get('utm_content');

    const persistAffiliateData = (affiliateData: AffiliateStoredData) => {
      localStorage.setItem(AFFILIATE_STORAGE_KEY, JSON.stringify(affiliateData));
      setCookie(AFFILIATE_STORAGE_KEY, affiliateData, AFFILIATE_COOKIE_OPTIONS);
      setAffiliateData(affiliateData);
    };

    const clearAffiliateData = () => {
      localStorage.removeItem(AFFILIATE_STORAGE_KEY);
      removeCookie(AFFILIATE_STORAGE_KEY);
    };

    const restoreAffiliateData = () => {
      const stored = localStorage.getItem(AFFILIATE_STORAGE_KEY);
      const fromStorage = parseAffiliateData(stored);

      if (fromStorage) {
        persistAffiliateData(fromStorage);
        return;
      }

      if (stored) {
        clearAffiliateData();
      }

      const fromCookie = parseAffiliateData(
        getCookie<AffiliateStoredData>(AFFILIATE_STORAGE_KEY),
      );

      if (fromCookie) {
        localStorage.setItem(AFFILIATE_STORAGE_KEY, JSON.stringify(fromCookie));
        setAffiliateData(fromCookie);
      }
    };

    const syncAffiliateData = async () => {
      let affiliateId = id;

      if (!affiliateId && affiliateSlug) {
        const affiliate = await getAffiliateByName(affiliateSlug);
        affiliateId = affiliate?.id || null;
      }

      if (affiliateId) {
        persistAffiliateData({
          soldById: affiliateId,
          capturedAt: new Date().toISOString(),
        });
        return;
      }

      restoreAffiliateData();
    };

    void syncAffiliateData();

    if (utm_source || utm_medium || utm_campaign || utm_term || utm_content) {
      const utmData = {
        ...(utm_source ? { utm_source } : {}),
        ...(utm_medium ? { utm_medium } : {}),
        ...(utm_campaign ? { utm_campaign } : {}),
        ...(utm_term ? { utm_term } : {}),
        ...(utm_content ? { utm_content } : {}),
        capturedAt: new Date().toISOString(),
      };

      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
      setUtmData(utmData);
    } else {
      const storedUtm = localStorage.getItem(UTM_STORAGE_KEY);
      if (storedUtm) {
        try {
          const data = JSON.parse(storedUtm);
          if (
            typeof data === 'object' &&
            data !== null &&
            typeof data.capturedAt === 'string'
          ) {
            const capturedAt = new Date(data.capturedAt);
            const now = new Date();
            const diff = now.getTime() - capturedAt.getTime();

            if (diff < HOURS_48_MS) {
              setUtmData(data);
            } else {
              localStorage.removeItem(UTM_STORAGE_KEY);
            }
          } else {
            localStorage.removeItem(UTM_STORAGE_KEY);
          }
        } catch (error) {
          console.error('Error parsing UTM data from localStorage:', error);
          localStorage.removeItem(UTM_STORAGE_KEY);
        }
      }
    }
  }, [searchParams, setAffiliateData, setUtmData]);

  return null;
}
